import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  allTimezones,
  ILabelStyle,
  useTimezoneSelect,
} from 'react-timezone-select';
import { useCustomTimeZoneSettings } from 'src/hooks/api/useCustomTimeZoneSettings';

import { DEFAULT_TIMEZONE_LOCAL } from './SiteTimezoneContext.constants';

/**
 * Calculate timezone offset from timezone name using react-timezone-select.
 * This uses the same calculation as TimezoneSection.tsx when saving timezone settings.
 *
 * @param timeZone - IANA timezone name (e.g., 'America/New_York')
 * @param parseTimezone - The parseTimezone function from react-timezone-select
 * @returns Offset in minutes from UTC
 */
const getOffsetFromTimezone = (
  timeZone: string,
  parseTimezone: ReturnType<typeof useTimezoneSelect>['parseTimezone']
): number => {
  try {
    const timezone = parseTimezone(timeZone);
    if (timezone.offset != null) {
      // Use the same calculation as TimezoneSection.tsx:41
      // Convert hours to minutes and apply negative sign
      return -timezone.offset * 60;
    }
    // Fallback to browser's local timezone if offset is null
    return new Date().getTimezoneOffset();
  } catch {
    // Fallback to browser's local timezone if parsing fails
    return new Date().getTimezoneOffset();
  }
};

type SiteTimezoneContextType = {
  timeZone: string;
  timezoneOffsetMins: number;
  isLoading: boolean;
  toZonedTime: (date: Date) => Date;
  fromZonedTime: (date: Date) => Date;
};

const SiteTimezoneContextDefaults = {
  timeZone: DEFAULT_TIMEZONE_LOCAL,
  timezoneOffsetMins: new Date().getTimezoneOffset(),
  isLoading: false,
  toZonedTime: (date: Date) => date,
  fromZonedTime: (date: Date) => date,
};

const SiteTimezoneContext = createContext<SiteTimezoneContextType>(
  SiteTimezoneContextDefaults
);

type SiteTimezoneContextProviderType = {
  children: ReactNode;
};

export const SiteTimezoneContextProvider = ({
  children,
}: SiteTimezoneContextProviderType) => {
  const { customTimeZoneSettings, isLoading } = useCustomTimeZoneSettings();

  const selectOptions = {
    labelStyle: 'original' as ILabelStyle,
    timezones: allTimezones,
  };

  const { parseTimezone } = useTimezoneSelect(selectOptions);

  const timeZone = customTimeZoneSettings?.value ?? DEFAULT_TIMEZONE_LOCAL;

  // Calculate offset dynamically based on timezone name (handles DST automatically)
  // Uses the same calculation as TimezoneSection when saving to database
  const timezoneOffsetMins = useMemo(
    () => getOffsetFromTimezone(timeZone, parseTimezone),
    [timeZone, parseTimezone]
  );

  const toZonedTimeWrapper = useCallback(
    (date: Date) => {
      return toZonedTime(date, timeZone);
    },
    [timeZone]
  );

  const fromZonedTimeWrapper = useCallback(
    (date: Date) => {
      return fromZonedTime(date, timeZone);
    },
    [timeZone]
  );

  const contextValue = useMemo(
    () => ({
      timeZone,
      timezoneOffsetMins,
      isLoading,
      toZonedTime: toZonedTimeWrapper,
      fromZonedTime: fromZonedTimeWrapper,
    }),
    [
      fromZonedTimeWrapper,
      isLoading,
      timeZone,
      timezoneOffsetMins,
      toZonedTimeWrapper,
    ]
  );

  return (
    <SiteTimezoneContext.Provider value={contextValue}>
      {children}
    </SiteTimezoneContext.Provider>
  );
};

export const useSiteTimezoneContext = () => useContext(SiteTimezoneContext);
